import { type JSX, type ReactNode, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { type Suchart, type Verlaufseintrag, verlauf as holeVerlauf } from '../api/suche';
import {
  AUFGABEN,
  type Akteziel,
  type Anordnung,
  type Feld,
  type Groesse,
  BERICHTSWIDGET,
  VERLAUF,
  anordnen,
  anordnung as holeAnordnung,
  aufVorgabe,
  uebersicht as holeUebersicht,
} from '../api/uebersicht';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Meldung } from '../komponenten/Meldung';
import { Sinnbild } from '../komponenten/Sinnbild';
import { Berichtswidget } from './Berichtswidget';
import { EINFUEGEN_ID, Feldraster } from './Feldraster';
import { Aufgabenwidget, Kachelwidget, Skelett, Verlaufswidget } from './Widget';

/**
 * Der Arbeitsplatz — die Seite, die morgens zuerst aufgeht (M-02, SYS-12).
 *
 * ============================================================================
 * DREI FRAGEN — UND WO SIE STEHEN, BESTIMMT JEDER SELBST
 * ============================================================================
 *
 *   1. WAS IST ZU TUN? „Das steht an" — in der Vorgabe oben links. Gibt es
 *      nichts, steht das Feld auch nicht da, und das ist die beste Auskunft
 *      des Tages.
 *   2. WIE STEHT ES? Die Kacheln.
 *   3. WORAN WAR ICH DRAN? Der Verlauf (M-07).
 *
 * Seit Auftrag #35 sind alle drei Widgets in einem freien Raster aus vier
 * Spalten (`Feldraster.tsx`). Die Vorgabe der API legt sie in dieser Folge
 * aus; jeder verschiebt, bemisst, fügt ein und entfernt selbst.
 *
 * ============================================================================
 * WAS HIER *NICHT* MEHR STEHT
 * ============================================================================
 *
 * Die Seite hieß „Start" und zeigte das eigene Konto, die eigenen Rechte und
 * die eigenen Sitzungen. Das war richtig, solange es sonst nichts gab; es ist
 * aber keine Auskunft über die ARBEIT, sondern über den ZUGANG. Beides steht
 * jetzt unter „Mein Konto", wo die Sitzungen ohnehin schon standen.
 *
 * ============================================================================
 * WAS DIE MASKE NICHT SELBST ENTSCHEIDET
 * ============================================================================
 *
 * WELCHE Kacheln kommen. Das entscheidet die API anhand der wirksamen Rechte
 * (SYS-12, 4.3) — hier steht keine einzige Rechteabfrage. Eine Auswahl in der
 * Oberfläche wäre die zweite Rechnung derselben Frage, und die beiden liefen
 * beim ersten Zusatzrecht auseinander.
 *
 * ============================================================================
 * FREI ANGEORDNET (SYS-12 i. d. F. AeA-40, Entwurf; Auftrag #35)
 * ============================================================================
 *
 * Kacheln, Verlauf, gespeicherte Berichte (Auftrag #27) und „Das steht an"
 * sind Widgets mit einer Lage im Raster. Die Anordnung liegt auf dem Server,
 * nicht im Browser; ohne eigene Anordnung schickt die API die Vorgabe.
 *
 * „DAS STEHT AN" IST EIN WIDGET (Auftrag #35). Bis dahin stand die Liste fest
 * oben, weil eine weggeklickte Liste den überfälligen Posten von morgen
 * verschwiege. Der Auftraggeber wollte sie entfernbar; die Vorgabe trägt sie
 * weiter (SYS-12), und wer sie entfernt, wird vorher gefragt und erfährt,
 * was dann fehlt.
 */
export function Arbeitsplatz({
  beiBereich,
  beiVorgang,
  beiBericht,
  beiAkte,
}: {
  /** Auftrag #37: ein Fall aus der Liste eines Widgets öffnet seine Akte. */
  readonly beiAkte: (ziel: Akteziel) => void;
  /** Auftrag #27: ein Bericht-Widget öffnet seinen Bericht. */
  readonly beiBericht: (id: string) => void;
  /** Sprung in einen Menübereich — die Kachel führt dorthin, wo die Zahl herkommt. */
  readonly beiBereich: (kennung: string) => void;
  /** Sprung in einen Datensatz aus dem Verlauf. Gibt zurück, ob es die Maske gibt. */
  readonly beiVorgang: (art: Suchart, id: string) => boolean;
}): JSX.Element {
  /** Der jüngste Stand einer gemessenen Kachel (ISO). */
  const [stand, setzeStand] = useState<string | undefined>(undefined);
  /** Jedes „Neu messen" zählt hoch; jede Kachel lädt dann für sich neu. */
  const [runde, setzeRunde] = useState(0);
  /** Kacheln, die gemessen sind und nichts zu zeigen haben — sie fallen heraus. */
  const [leer, setzeLeer] = useState<ReadonlySet<string>>(new Set());
  const [letzte, setzeLetzte] = useState<readonly Verlaufseintrag[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laedt, setzeLaedt] = useState(true);
  const [folge, setzeFolge] = useState<Anordnung | undefined>(undefined);
  const [anpassen, setzeAnpassen] = useState(false);
  const [speichert, setzeSpeichert] = useState(false);
  const [ansage, setzeAnsage] = useState('');

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      /*
       * Beide zugleich und nicht nacheinander: Es sind zwei unabhängige
       * Auskünfte, und nacheinander wäre die Seite doppelt so lange leer
       * (NFA-01). Die Kacheln lädt jede für sich, sobald die Anordnung steht
       * (Auftrag CRM-165): eine langsame hält die anderen nicht auf.
       */
      const [gesehen, angeordnet] = await Promise.all([holeVerlauf(), holeAnordnung()]);
      // Auftrag #38: alle — die Karte zeigt, so viele passen (#41: ohne „+ N weitere").
      setzeLetzte(gesehen);
      setzeFolge(angeordnet);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const neuMessen = (): void => {
    setzeLeer(new Set());
    setzeRunde((r) => r + 1);
    void laden();
  };

  const gemessen = useCallback((kennung: string, da: boolean, wann: string): void => {
    setzeStand((bisher) => (bisher === undefined || wann > bisher ? wann : bisher));
    if (!da) setzeLeer((bisher) => new Set(bisher).add(kennung));
  }, []);

  /** SYS-12 i. d. F. AeA-40: jede Änderung sofort auf den Server. */
  const aendern = async (auftrag: () => Promise<Anordnung>, text: string): Promise<void> => {
    setzeSpeichert(true);
    try {
      setzeFolge(await auftrag());
      setzeAnsage(text);
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeSpeichert(false);
    }
  };

  const eigene = folge?.eigene === true;
  /* Auftrag #35: ab 1024 px gilt die Lage und darf angepasst werden. */
  const frei = useBreit();
  const [auswahl, setzeAuswahl] = useState(false);

  /**
   * Was im Feld steht, oder `null`, wenn es gerade nichts zu zeigen gibt.
   * Auftrag #37: Die Größe des Felds bestimmt, wie viel die Karte zeigt.
   */
  const inhalt = (feld: Feld): ReactNode | null => {
    const groesse = { breite: feld.breite, hoehe: feld.hoehe };
    /*
     * Gemessen und nichts zu zeigen: Eine gewählte Kachel, die die API nicht
     * schickt (kein Recht mehr, oder „nur bei Bedarf" und nichts zu zeigen),
     * fällt still heraus — die Maske zeigt nie mehr, als die API geschickt hat.
     */
    if (leer.has(feld.kennung)) return null;
    if (feld.kennung === AUFGABEN) {
      return <Aufgabenfeld key={runde} groesse={groesse} beiBereich={beiBereich} beiGemessen={gemessen} />;
    }
    if (feld.kennung === VERLAUF) return letzte.length > 0 ? <Verlaufswidget letzte={letzte} beiVorgang={beiVorgang} /> : null;
    if (feld.kennung.startsWith(BERICHTSWIDGET)) {
      const id = feld.kennung.slice(BERICHTSWIDGET.length);
      return <Berichtswidget id={id} groesse={groesse} beiOeffnen={() => beiBericht(id)} />;
    }
    return (
      <Kachelfeld
        key={runde}
        kennung={feld.kennung}
        groesse={groesse}
        beiGemessen={gemessen}
        beiBereich={beiBereich}
        beiAkte={beiAkte}
      />
    );
  };

  return (
    /* Seitentyp Übersicht (ÄA-41): Karten und Kacheln in einem Raster. */
    <div className="uebersicht">
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {/* Auftrag #27: ein gelöschter oder nicht mehr geteilter Bericht — einmal gesagt. */}
      {folge?.hinweise.map((h) => (
        <p key={h} className="hinweiszeile" role="status">
          {h}
        </p>
      ))}

      {laedt && folge === undefined && (
        /* Auftrag #37: Laden als Skelett in der Form des Rasters statt „Wird geladen …". */
        <div className="feldraster feldraster--skelett" aria-busy="true">
          <p className="nur-vorlesen" role="status">
            Das Dashboard wird geladen.
          </p>
          {[2, 1, 1, 1, 1, 2].map((breite, i) => (
            <div key={i} className={`rasterfeld rasterfeld--b${String(breite)}`}>
              <Skelett art="karte" />
            </div>
          ))}
        </div>
      )}

      {/*
        Leer, weil das Konto nichts sehen darf — nicht, weil der Benutzer
        alles entfernt hat (das sagt der Satz weiter unten) und nicht, weil
        nur ein Bericht auf dem Dashboard liegt. Seit Auftrag #37 misst die
        API nur, was auf dem Dashboard liegt; entscheidend ist deshalb, was
        überhaupt zur Verfügung stünde.
      */}
      {!laedt && !anpassen
        && (folge?.felder.every((f) => f.kennung !== AUFGABEN) === true || leer.has(AUFGABEN))
        && (folge?.verfuegbar ?? []).every((w) => w.kennung === AUFGABEN || w.kennung === VERLAUF) && (
        <div className="karte">
          <p className="hinweiszeile">
            Für Ihr Konto sind noch keine Bereiche freigeschaltet. Die Administration kann
            Ihnen eine Rolle zuordnen; danach steht hier, was für Sie ansteht.
          </p>
        </div>
      )}

      {anpassen && frei && folge !== undefined && (
        /* Die Leiste des Anpassens (SYS-12 i. d. F. AeA-40, Auftrag #35). */
        <div className="uebersicht__anpassen">
          <p className="hinweiszeile">
            Ziehen Sie ein Feld am Griff an eine andere Stelle oder an der Ecke unten rechts
            größer und kleiner — oder nutzen Sie sein Menü. Die anderen weichen aus, Lücken
            schließen sich nach oben. Ihre Anordnung gilt für Ihr Konto auf jedem Gerät.
          </p>
          <div className="knopfreihe">
            <button
              id={EINFUEGEN_ID}
              className="knopf"
              type="button"
              aria-haspopup="dialog"
              disabled={speichert}
              onClick={() => setzeAuswahl(true)}
            >
              <Sinnbild gewicht="light" name="plus" /> Widget oder Bericht einfügen
            </button>
            {folge.eigene && (
              <button
                className="knopf"
                type="button"
                disabled={speichert}
                onClick={() => void aendern(aufVorgabe, 'Die Vorgabe gilt wieder.')}
              >
                Auf Vorgabe zurücksetzen
              </button>
            )}
          </div>
        </div>
      )}

      {folge !== undefined && (
        <Feldraster
          anordnung={folge}
          frei={frei}
          anpassen={anpassen}
          beschaeftigt={speichert}
          inhalt={inhalt}
          beiAenderung={(felder, text) => void aendern(() => anordnen(felder), text)}
          auswahlOffen={auswahl}
          beiAuswahl={setzeAuswahl}
        />
      )}

      {eigene && folge!.felder.length === 0 && (
        <p className="hinweiszeile">
          Sie haben alle Widgets entfernt. Unter „Dashboard anpassen" fügen Sie wieder welche ein.
        </p>
      )}

      <p className="nur-vorlesen" role="status">
        {ansage}
      </p>

      {folge !== undefined && (
        <p className="uebersicht__leiste">
          {stand !== undefined && <>Stand {alsUhrzeit(stand)}.{' '}</>}
          <button className="knopf knopf--klein" type="button" onClick={neuMessen}>
            Neu messen
          </button>{' '}
          {folge !== undefined && frei && (
            <button
              className="knopf knopf--klein"
              type="button"
              aria-expanded={anpassen}
              onClick={() => setzeAnpassen((offen) => !offen)}
            >
              {anpassen ? 'Anpassen beenden' : 'Dashboard anpassen'}
            </button>
          )}
          {/* Auftrag #35: Angepasst wird in der Breite — auf dem Handy liegt das Raster in Lesefolge. */}
          {folge !== undefined && !frei && <span>Angepasst wird das Dashboard ab 1024 Pixel Breite.</span>}
        </p>
      )}
    </div>
  );
}

/**
 * Eine Kachel, für sich geladen (NFA-01, Auftrag CRM-165): Skelett, nach drei
 * Sekunden der Hinweis, nach fünfzehn oder bei Fehler „Erneut versuchen" —
 * nur für diese Kachel. Schickt die API sie nicht, meldet das Feld „leer".
 */
function Kachelfeld({
  kennung,
  groesse,
  beiGemessen,
  beiBereich,
  beiAkte,
}: {
  readonly kennung: string;
  readonly groesse: Groesse;
  readonly beiGemessen: (kennung: string, da: boolean, stand: string) => void;
  readonly beiBereich: (kennung: string) => void;
  readonly beiAkte: (ziel: Akteziel) => void;
}): JSX.Element | null {
  const zustand = useLadestufe((signal) => holeUebersicht({ nur: kennung, signal }), kennung);
  const kachel = zustand.daten?.kacheln.find((k) => k.kennung === kennung);
  const stand = zustand.daten?.stand;
  useEffect(() => {
    if (stand !== undefined) beiGemessen(kennung, kachel !== undefined, stand);
  }, [kennung, kachel, stand, beiGemessen]);

  if (zustand.stufe !== 'fertig') {
    return <Ladeanzeige zustand={zustand} skelett={<Skelett art="karte" />} was="Die Kachel" klasse="karte" />;
  }
  return kachel === undefined || stand === undefined ? null : (
    <Kachelwidget kachel={kachel} groesse={groesse} stand={stand} beiBereich={beiBereich} beiAkte={beiAkte} />
  );
}

/** „Das steht an", für sich geladen wie eine Kachel (Auftrag CRM-165). Ohne Aufgaben fällt es heraus. */
function Aufgabenfeld({
  groesse,
  beiBereich,
  beiGemessen,
}: {
  readonly groesse: Groesse;
  readonly beiBereich: (kennung: string) => void;
  readonly beiGemessen: (kennung: string, da: boolean, stand: string) => void;
}): JSX.Element | null {
  const zustand = useLadestufe((signal) => holeUebersicht({ nur: AUFGABEN, signal }));
  const aufgaben = zustand.daten?.aufgaben;
  const stand = zustand.daten?.stand;
  useEffect(() => {
    if (stand !== undefined && aufgaben !== undefined) beiGemessen(AUFGABEN, aufgaben.length > 0, stand);
  }, [aufgaben, stand, beiGemessen]);

  if (zustand.stufe !== 'fertig') {
    return <Ladeanzeige zustand={zustand} skelett={<Skelett art="karte" />} was="„Das steht an“" klasse="karte" />;
  }
  return aufgaben === undefined || aufgaben.length === 0 ? null : (
    <Aufgabenwidget aufgaben={aufgaben} groesse={groesse} beiBereich={beiBereich} />
  );
}

/**
 * Ab 1024 px Breite (Auftrag #35): vier Spalten, die Lage gilt, und es darf
 * angepasst werden. Ohne `matchMedia` (Prüfumgebung) gilt die breite Ansicht.
 */
const BREIT = '(min-width: 1024px)';

function useBreit(): boolean {
  const [breit, setzeBreit] = useState(() =>
    typeof window.matchMedia === 'function' ? window.matchMedia(BREIT).matches : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const abfrage = window.matchMedia(BREIT);
    const beiWechsel = (): void => setzeBreit(abfrage.matches);
    beiWechsel();
    abfrage.addEventListener('change', beiWechsel);
    return () => abfrage.removeEventListener('change', beiWechsel);
  }, []);
  return breit;
}

/** Ortszeit (FRS-01). Die Zone steht fest und kommt nicht aus dem Browser. */
const UHRZEIT = new Intl.DateTimeFormat('de-DE', {
  timeStyle: 'short',
  timeZone: 'Europe/Berlin',
});

function alsUhrzeit(iso: string): string {
  return `${UHRZEIT.format(new Date(iso))} Uhr`;
}
